import { createMultifileUploadSlice, speedStream, stagedFiles } from "@nova/slice-multifile-upload"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { ID, ImageProcessingState, ImageType, ImageWarnings, JobStatusType } from "@nova/constants"
import { getFrameParser } from "../processing/getFrameParser"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { createColorFinderImage } from "features/pubsub/entityActions/images/colorFinder"
import { ColorFinderImage } from "@nova/protobuf"
import { dateAsNumber } from "@nova/serializer/src/client/util"
import { debounce, memoize } from "nodash"
import { selectImageColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { createIdFromFile, getEnumValue } from "@nova/util-strings"
import { getNumberOfWorkers, runTask } from "@nova/webworker-pool"
import { processImage } from "integrations/sensortool/processImage"
import { FlightId } from "features/map/layers/types"
import { SuperIndexController } from "../processing/SuperIndexController.class"
import { buildColorIndex } from "@nova/component-color-finder-legend/src/colorspace"
import {
    createCumulativeRgb,
    CumulativeRGB,
} from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"
import { updateImageColorFinderFlight } from "features/pubsub/entityActions/flights/imageColorFinder"
import store from "integrations/redux/store"
import { uploadAnomalyData, uploadColorData } from "./ImageColorFinderUploads"
import { emitLayerProcessed } from "../emitLayerProcessed"
import { uint32ToUint8Array } from "tools/util-buffers"
import { invalidateColorTilerCache, sortColorFinderImages } from "../V2/ColorFinderQueries"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { IngestDataType } from "@nova/schemas"
import { AutoProcessController } from "panels/content/side-panel/upload-flow/preview-files/AutoProcessController"
import { createSpeedCallback } from "themes/default-tailwind/panels/GlobalUploadContainer"

export type ImageColorFinderJob = {
    id: ID
    status: JobStatusType
    createdAt: string
}
export type ImageColorFinderCreateArgs = {
    flightId: string
    fireId: string
}

type ColorFinderMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
}

export type ColorFinderState = {
    id: string
    name: string
    s3Key?: string
    state: ImageProcessingState
    warnings?: ImageWarnings
    deletedAt?: string
    colorData?: Uint8Array
    takenAt?: number
    unsupported?: boolean
    longitude?: number
    latitude?: number
    altitude?: number
    pitch?: number
    yaw?: number
    roll?: number
    focalLength?: number
    fov?: number[]
    height?: number
    hasLowQuality?: boolean
    fileName?: string
    make?: string
    size?: number
    hasThermalData?: boolean
    flightId?: string
    imageHeight?: number
    imageWidth?: number
    imageType?: number
    uploadedAt?: number
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectSet,
        selectAllUploads,
        selectUpload,
        selectUploadsForSet,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"colorfinder-images-v1", ColorFinderState, ColorFinderMeta>(
    "colorfinder-images-v1",
    (m: ColorFinderMeta) =>
        ({
            id: createIdFromFile(m.file),
            name: m.file.name,
            state: ImageProcessingState.Pending,
        }) satisfies ColorFinderState,
    {
        addUploadToProject: (setId, upload) =>
            createColorFinderImage({
                data: upload as ColorFinderImage,
                context: { flightId: setId },
            }),
    },
    async (state, meta, update, { useSignal, useSemaphore }) => {
        const file = meta.file as ImageColorFinderFile

        const uploadSignal = useSignal("upload", meta.setID)
        const deleteSignal = useSignal("delete", state.id)
        deleteSignal.then(() => {
            if (state.state !== ImageProcessingState.UploadDone)
                state = { ...state, deletedAt: new Date().toISOString() }
            AutoProcessController.emit(meta.flightID + "imageDeleted", {
                imageId: file.frameNumber,
            })
        })

        await useSemaphore(getNumberOfWorkers() * 3, async () => {
            update({ ...state, state: ImageProcessingState.Processing })
            if (state.deletedAt) return
            const fileBuffer = await meta.file.arrayBuffer()
            const metadata = await runTask(processImage, { fileBuffer }, { transfer: [fileBuffer] })

            state = {
                ...state,
                ...metadata?.gps,
                flightId: meta.flightID,
                takenAt: dateAsNumber(metadata?.takenAt),
                ...metadata?.pyr,
                height: metadata?.height,
                fov: Array.from(metadata?.fov || []),
                focalLength: metadata?.focalLength,
                state: ImageProcessingState.Pending,
                fileName: file.name,
                size: file.size,
                make: metadata?.make,
                imageHeight: metadata?.imageHeight,
                imageWidth: metadata?.imageWidth,
                imageType: getEnumValue(ImageType, file.name.split(".").at(-1) || ""),
                hasThermalData: metadata?.hasThermalData,
                colorData: new Uint8Array(0),
                warnings: {},
            }
            update({ ...state }, () => AutoProcessController.emit(meta.flightID + "imageStaged"))
        })

        // this waits for the upload action to be fired by the UI
        if (state.deletedAt) return
        await uploadSignal
        if (state.deletedAt) return
        state = {
            ...state,
            state: ImageProcessingState.PendingUpload,
        }
        update({ ...state })
        // semaphore lock - revisit https://en.wikipedia.org/wiki/Semaphore_(programming) before poking around it this
        // 5 is the number of uploads that are allowed to be in progress at once. As soon as one finishes, it'll start the next

        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = true
            return true
        }
        window.addEventListener("beforeunload", onBeforeUnload)
        await useSemaphore(15, async () => {
            if (state.deletedAt) return
            state = { ...state, state: ImageProcessingState.Uploading }
            update(state)
            await fileManagementApi.putFile(meta.file, {
                filename: state.id,
                tags: {
                    dataType: IngestDataType.ColorFinderImage,
                    projectId: meta.fireID,
                    flightId: meta.flightID,
                    orgId: meta.organizationID,
                },
                maxQueue: 5,
                onProgress: createSpeedCallback(({ speedMbps }) => {
                    speedStream.setSpeed(meta.setID, state.id, speedMbps)
                }),
            })
            if (state.deletedAt) return

            state = {
                ...state,
                state: ImageProcessingState.UploadDone,
                uploadedAt: dateAsNumber(),
            }

            update({ ...state })
        })
        window.removeEventListener("beforeunload", onBeforeUnload)
        return { ...state }
    }
)

export const localImageColorDataMap: Record<
    FlightId,
    {
        colorData: Uint32Array[]
        anomalyIndex?: Uint8Array
        cumulativeRgb?: CumulativeRGB
    }
> = {}

type ImageColorFinderFile = File & {
    getController: () => Promise<SuperIndexController>
    frameNumber: number
}

export const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    onFinish?: () => void
) => {
    let stageCount = 0

    AutoProcessController.on(flightID + "imageStaged", async () => {
        ++stageCount
        if (stageCount < files.length) return

        processImages()
    })

    const processImages = debounce(async () => {
        const uploads = (selectUploadsForSet(flightID)(store.getState()) ??
            []) as ColorFinderState[]
        const staged = sortColorFinderImages(uploads)
        if (!staged || !staged.length) return

        staged.forEach((f) =>
            store.dispatch(actions.update({ ...f, state: ImageProcessingState.Pending }))
        )

        const newSuperIndexController = new SuperIndexController(
            await getFrameParser(),
            staged.length
        )

        const BATCH_SIZE = 5

        for (let i = 0; i < staged.length; i += BATCH_SIZE) {
            const batch = staged.slice(i, i + BATCH_SIZE).map((f) => stagedFiles[f.id])

            await Promise.all(
                batch.map(async (file, batchIndex) => {
                    const globalIndex = i + batchIndex

                    let bitmap: ImageBitmap | undefined
                    let videoFrame: VideoFrame | undefined
                    try {
                        bitmap = await createImageBitmap(file)
                        videoFrame = new VideoFrame(bitmap, { timestamp: 0 })
                        await newSuperIndexController.parseColorspace(videoFrame, globalIndex)

                        store.dispatch(
                            actions.update({
                                ...staged[globalIndex],
                                state: ImageProcessingState.ProcessingDone,
                            })
                        )
                    } catch (err) {
                        console.error(
                            "@color-finder image decode failed",
                            staged[globalIndex]?.id,
                            err
                        )
                        store.dispatch(
                            actions.update({
                                ...staged[globalIndex],
                                state: ImageProcessingState.ProcessingDone,
                                unsupported: true,
                            })
                        )
                    } finally {
                        videoFrame?.close()
                        bitmap?.close()
                    }
                })
            )
        }

        const superIndex = newSuperIndexController.getZoomBuffers().at(-1)!
        const colorIndex = buildColorIndex(superIndex)

        const anomalyIndex = await newSuperIndexController.getParser().finishAndGetAnomalyScores()
        const cumulative = await createCumulativeRgb({ colorIndex: colorIndex })

        localImageColorDataMap[flightID] = {
            colorData: newSuperIndexController.getZoomBuffers(),
            anomalyIndex: anomalyIndex ?? undefined,
            cumulativeRgb: cumulative,
        }

        invalidateColorTilerCache()
    }, 50)

    AutoProcessController.on(flightID + "imageDeleted", async () => {
        --stageCount
        processImages()
    })

    const meta = new Array<ColorFinderMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({
        meta,
        onFinish: async () => {
            const localData = localImageColorDataMap[flightID]
            if (localData && localData.anomalyIndex && localData.cumulativeRgb) {
                try {
                    const path = await uploadAnomalyData(localData.anomalyIndex, {
                        flightId: flightID,
                        projectId: fireID,
                        workspaceId: organizationID,
                    })
                    const colorDataPath = await uploadColorData(localData.colorData.at(-1)!, {
                        flightId: flightID,
                        projectId: fireID,
                        workspaceId: organizationID,
                    })

                    delete localImageColorDataMap[flightID]

                    store.dispatch(
                        updateImageColorFinderFlight({
                            id: flightID,
                            updates: {
                                anomalyPath: path,
                                cumulativeColorIndex: uint32ToUint8Array(
                                    localData.cumulativeRgb.cumulativeIndex
                                ),
                                colorDataPath,
                            },
                        })
                    )

                    emitLayerProcessed(
                        organizationID,
                        fireID,
                        flightID,
                        "imageColorFinderFlights",
                        true
                    )
                } catch (err) {
                    console.error("@color-finder image", err)
                    emitLayerProcessed(
                        organizationID,
                        fireID,
                        flightID,
                        "imageColorFinderFlights",
                        false
                    )
                    throw err
                }
            }
            onFinish?.()
        },
    })
}

export const uploadFlight = (id: string) => actions.signal("upload", id)
export const deleteUpload = actions.deleteUpload
export const deleteUploadSet = actions.deleteUploadSet

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.UploadDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

type UIState = {
    activeFlightId?: string
    playing: boolean
    splitButtonOpen: boolean
    statusCheckerOpen: boolean
    selectedUpload: Record<string, boolean>
    currentImageColorFinderId: string
    selectedFrame: number
}

const uiStateName = "image-colorFinder-ui-v1"
type uiStateNameType = typeof uiStateName

const initialState: UIState = {
    playing: false,
    splitButtonOpen: false,
    selectedUpload: {},
    statusCheckerOpen: false,
    currentImageColorFinderId: "",
    selectedFrame: 1,
}

export const selectImageColorFinderMockJob = memoize((id: string) =>
    createSelector([selectImageColorFinderFlightById(id)], (flight) => {
        if (flight?.images.length > 0) return { status: JobStatusType.COMPLETED }
    })
)

const {
    reducer: uiStateReducer,
    actions: {
        setPlaying,
        setSplitButtonOpen,
        setStatusCheckerOpen,
        setSelectedUpload,
        setSelectedFrame,
    },
} = createSlice({
    name: uiStateName,
    initialState,
    reducers: {
        setSelectedFrame: (state, action: { payload: number }) => {
            state.selectedFrame = action.payload
        },
        setPlaying: (state, action: { payload: boolean }) => {
            state.playing = action.payload
        },
        setSplitButtonOpen: (state, action) => {
            state.splitButtonOpen = action.payload
        },
        setStatusCheckerOpen: (state, action) => {
            state.statusCheckerOpen = action.payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
    },
})

const {
    selectPlaying,
    selectSplitButtonOpen,
    selectSelectedUpload,
    selectStatusCheckerOpen,
    selectCurrentImageColorFinderId,
    selectSelectedFrame,
} = withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
    selectPlaying: () => (s) => s.playing,
    selectSplitButtonOpen: () => (s) => s.splitButtonOpen,
    selectStatusCheckerOpen: () => (s) => s.statusCheckerOpen,
    selectSelectedUpload: () => (s) => s.selectedUpload,
    selectCurrentImageColorFinderId: () => (s) => s.currentImageColorFinderId,
    selectSelectedFrame: () => (s) => s.selectedFrame,
})

// export selectors
export {
    selectCurrentImageColorFinderId,
    selectSet,
    selectUpload,
    selectAllUploads,
    selectUploadsForSet,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectSplitButtonOpen,
    selectStatusCheckerOpen,
    selectSelectedUpload,
    selectPlaying,
    selectSelectedFrame,
}

// export actions
export { setPlaying, setSplitButtonOpen, setStatusCheckerOpen, setSelectedUpload, setSelectedFrame }

// export reducer
export const reducer = {
    ...imagesReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }
